import { HoverPopover } from '@app/components/base/HoverPopover';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Box, Chip, IconButton } from '@mui/material';
import { FormattedMessage } from 'react-intl';

export interface FragmentCardTagsProps {
  tags: string[];
  /** When true, shows the tag icon (e.g. as trigger or next to overflow). Default true. */
  showIcon?: boolean;
  /**
   * Max number of tags to show inline as chips. When there are more tags,
   * the first maxVisible are shown and the rest are in a popover.
   * When undefined, all tags are shown in a popover (no inline chips).
   */
  maxVisible?: number;
}

const iconButtonSx = {
  p: 0.25,
  color: 'grey.500',
  minWidth: 24,
  minHeight: 24,
  flexShrink: 0,
};

/**
 * Renders fragment tags either inline as chips and/or in a popover on hover.
 * Renders nothing when tags is empty.
 */
export function FragmentCardTags({ tags, showIcon = true, maxVisible }: FragmentCardTagsProps) {
  if (tags.length === 0) return null;

  const visibleTags = maxVisible !== undefined ? tags.slice(0, maxVisible) : [];
  const overflowTags = maxVisible !== undefined ? tags.slice(maxVisible) : tags;
  const hasOverflow = overflowTags.length > 0;

  const tagsContent = (tagList: string[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {tagList.map((label, i) => (
        <Chip key={i} label={label} size="small" />
      ))}
    </Box>
  );

  const popoverTitle = <FormattedMessage id="fragments.props.tags" defaultMessage="Tags" />;

  /** Label for the popover trigger when not using the icon: count or "+N". */
  const triggerLabel = maxVisible === undefined ? tags.length : `+${overflowTags.length}`;

  const popoverTrigger = showIcon ? (
    <IconButton size="small" sx={iconButtonSx}>
      <LocalOfferIcon sx={{ fontSize: '16px' }} />
    </IconButton>
  ) : (
    <Chip size="small" label={triggerLabel} sx={{ height: 20, fontSize: '0.75rem' }} />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        ml: 'auto',
        // Keep the tag icon on the same footer row when the meta text is long.
        flexShrink: 0,
      }}
    >
      {showIcon && !hasOverflow && (
        <LocalOfferIcon sx={{ fontSize: '16px', color: 'grey.400', flexShrink: 0 }} />
      )}
      {visibleTags.map((label, i) => (
        <Chip key={i} label={label} size="small" />
      ))}
      {hasOverflow && (
        <HoverPopover enabled title={popoverTitle} trigger={popoverTrigger}>
          {tagsContent(overflowTags)}
        </HoverPopover>
      )}
    </Box>
  );
}
